// Путь: D:\QR\QR-WEB\dashboard\src\components\Dashboard\AdminDashboard.jsx
// Название: Admin Dashboard - Strategy 2026
// Версия: 7.0.0 (UI polish, readability, formatting)
// Дата: 29.01.2026

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [subscriptions, setSubscriptions] = useState({ stats: {} });
  const [wallets, setWallets] = useState({ stats: {} });
  const [leads, setLeads] = useState({ stats: {} });
  const [commissions, setCommissions] = useState({ stats: {} });
  const [recentCampaigns, setRecentCampaigns] = useState([]);
  const [recentLeads, setRecentLeads] = useState([]);

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    setLoading(true);
    await Promise.all([
      loadStats(),
      loadSubscriptions(),
      loadWallets(),
      loadLeads(),
      loadCommissions(),
      loadRecentCampaigns(),
      loadRecentLeads(),
    ]);
    setLoading(false);
  };

  const loadStats = async () => {
    try {
      const res = await api.get('/admin/stats');
      if (res.success) setStats(res.data);
    } catch (err) {
      console.error('Stats error:', err);
    }
  };

  const loadSubscriptions = async () => {
    try {
      const res = await api.get('/admin/subscriptions?limit=5');
      if (res.success) setSubscriptions(res);
    } catch (err) {
      console.error('Subscriptions error:', err);
    }
  };

  const loadWallets = async () => {
    try {
      const res = await api.get('/admin/wallets');
      if (res.success) setWallets(res);
    } catch (err) {
      console.error('Wallets error:', err);
    }
  };

  const loadLeads = async () => {
    try {
      const res = await api.get('/admin/leads?limit=5');
      if (res.success) setLeads(res);
    } catch (err) {
      console.error('Leads error:', err);
    }
  };

  const loadCommissions = async () => {
    try {
      const res = await api.get('/admin/commissions?limit=5');
      if (res.success) setCommissions(res);
    } catch (err) {
      console.error('Commissions error:', err);
    }
  };

  const loadRecentCampaigns = async () => {
    try {
      const res = await api.get('/admin/campaigns?limit=5');
      if (res.success) setRecentCampaigns(res.data || []);
    } catch (err) {
      console.error('Campaigns error:', err);
    }
  };

  const loadRecentLeads = async () => {
    try {
      const res = await api.get('/admin/leads?limit=4');
      if (res.success) setRecentLeads(res.leads || []);
    } catch (err) {
      console.error('Recent leads error:', err);
    }
  };

  // --- Formatting (no new data, just nicer presentation) ---
  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })
      : '—';

  const moneyFmt = useMemo(
    () => new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }),
    []
  );

  const formatMoney = (n) => moneyFmt.format(Number(n || 0));

  // Calculate MRR (same logic, just memoized)
  const mrr = useMemo(() => {
    const monthly = parseInt(subscriptions.stats?.monthly || 0, 10);
    const yearly = parseInt(subscriptions.stats?.yearly || 0, 10);
    return monthly * 249 + yearly * 207.5;
  }, [subscriptions.stats?.monthly, subscriptions.stats?.yearly]);

  // Growth badge
  const growthPercent = Number(commissions.stats?.growth_percent || 0);
  const growthClass = growthPercent >= 0 ? 'positive' : 'negative';

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-dashboard-loading" role="status" aria-live="polite">
          <div className="loader" aria-hidden="true"></div>
          <span>Загрузка данных…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <div className="dash-header">
        <div className="dash-title">
          <h1>Панель управления</h1>
          <span className="dash-subtitle">Strategy 2026 • Real-time Analytics</span>
        </div>

        <button className="refresh-btn" onClick={loadAll} type="button" aria-label="Обновить данные">
          Обновить
        </button>
      </div>

      {/* Revenue Hero */}
      <div className="revenue-hero">
        <div className="revenue-main">
          <span className="revenue-label">Общий доход</span>

          <div className="revenue-value-row">
            <span className="revenue-currency">€</span>
            <span className="revenue-value">{formatMoney(stats?.revenue?.total || 0)}</span>
          </div>

          <div className="revenue-breakdown">
            <span className="revenue-pill">
              Direct <b>€{formatMoney(stats?.revenue?.direct || 0)}</b>
            </span>
            <span className="revenue-pill">
              Referral <b>€{formatMoney(stats?.revenue?.referral || 0)}</b>
            </span>
          </div>
        </div>

        <div className="revenue-side">
          <div className="revenue-metric">
            <span className="metric-label">Эта неделя</span>
            <span className="metric-value">€{formatMoney(commissions?.stats?.this_week || 0)}</span>
            <span className={`growth-badge ${growthClass}`}>
              <span className="growth-arrow" aria-hidden="true">
                {growthPercent >= 0 ? '▲' : '▼'}
              </span>
              <span>{Math.abs(growthPercent)}%</span>
            </span>
          </div>

          <div className="revenue-metric">
            <span className="metric-label">MRR</span>
            <span className="metric-value">€{formatMoney(mrr)}</span>
            <span className="metric-sub">€249/mo • €2490/yr</span>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card purple" onClick={() => navigate('/admin/subscriptions')} role="button" tabIndex={0}>
          <div className="stat-icon" aria-hidden="true">💳</div>
          <div className="stat-content">
            <div className="stat-top">
              <span className="stat-value">{subscriptions.stats?.active || 0}</span>
              <span className="stat-label">Активных подписок</span>
            </div>

            <div className="stat-details">
              <span className="detail-item">
                <span className="detail-dot" aria-hidden="true">•</span> {subscriptions.stats?.trial || 0} trial
              </span>
              <span className="detail-item">
                <span className="detail-dot" aria-hidden="true">•</span> {subscriptions.stats?.monthly || 0} monthly
              </span>
            </div>
          </div>
        </div>

        <div className="stat-card green" onClick={() => navigate('/admin/wallets')} role="button" tabIndex={0}>
          <div className="stat-icon" aria-hidden="true">💰</div>
          <div className="stat-content">
            <div className="stat-top">
              <span className="stat-value">€{formatMoney(wallets.stats?.totalDeposit)}</span>
              <span className="stat-label">Brand Wallets</span>
            </div>

            <div className="stat-details">
              <span className="detail-item">
                <span className="detail-dot" aria-hidden="true">•</span> €{formatMoney(wallets.stats?.totalAvailable)} available
              </span>
              <span className="detail-item">
                <span className="detail-dot" aria-hidden="true">•</span> €{formatMoney(wallets.stats?.totalReserved)} reserved
              </span>
            </div>
          </div>
        </div>

        <div className="stat-card blue" onClick={() => navigate('/admin/leads')} role="button" tabIndex={0}>
          <div className="stat-icon" aria-hidden="true">👥</div>
          <div className="stat-content">
            <div className="stat-top">
              <span className="stat-value">{stats?.leads?.total || 0}</span>
              <span className="stat-label">Всего лидов</span>
            </div>

            <div className="stat-details">
              <span className="detail-item">
                <span className="detail-dot" aria-hidden="true">•</span> {stats?.leads?.direct || 0} direct (€20)
              </span>
              <span className="detail-item">
                <span className="detail-dot" aria-hidden="true">•</span> {stats?.leads?.referral || 0} referral (€20)
              </span>
            </div>
          </div>
        </div>

        <div className="stat-card orange" onClick={() => navigate('/admin/campaigns')} role="button" tabIndex={0}>
          <div className="stat-icon" aria-hidden="true">📋</div>
          <div className="stat-content">
            <div className="stat-top">
              <span className="stat-value">{stats?.campaigns?.total || 0}</span>
              <span className="stat-label">Кампаний</span>
            </div>

            <div className="stat-details">
              <span className="detail-item">
                <span className="detail-dot" aria-hidden="true">•</span> {stats?.campaigns?.gifts || 0} подарков
              </span>
              <span className="detail-item">
                <span className="detail-dot" aria-hidden="true">•</span> {stats?.campaigns?.claimed || 0} claimed
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Secondary Stats */}
      <div className="secondary-stats">
        <div className="mini-stat" onClick={() => navigate('/admin/users')} role="button" tabIndex={0}>
          <span className="mini-icon" aria-hidden="true">👤</span>
          <span className="mini-value">{stats?.users?.total || 0}</span>
          <span className="mini-label">Пользователей</span>
        </div>

        <div className="mini-stat" onClick={() => navigate('/admin/businesses')} role="button" tabIndex={0}>
          <span className="mini-icon" aria-hidden="true">🏢</span>
          <span className="mini-value">{stats?.users?.businesses || 0}</span>
          <span className="mini-label">Бизнесов</span>
        </div>

        <div className="mini-stat" onClick={() => navigate('/admin/promocodes')} role="button" tabIndex={0}>
          <span className="mini-icon" aria-hidden="true">🎟️</span>
          <span className="mini-value">{stats?.promocodes?.total || 0}</span>
          <span className="mini-label">Промокодов</span>
        </div>

        <div className="mini-stat" onClick={() => navigate('/admin/brand-gifts')} role="button" tabIndex={0}>
          <span className="mini-icon" aria-hidden="true">🎨</span>
          <span className="mini-value">{stats?.campaigns?.gifts || 0}</span>
          <span className="mini-label">AI Gifts</span>
        </div>

        <div className="mini-stat" role="button" tabIndex={0}>
          <span className="mini-icon" aria-hidden="true">📈</span>
          <span className="mini-value">{stats?.leads?.virality_percent || 0}%</span>
          <span className="mini-label">Virality</span>
        </div>
      </div>

      {/* Main Grid */}
      <div className="main-grid">
        {/* Recent Campaigns */}
        <div className="dash-card">
          <div className="card-header">
            <h3>Последние кампании</h3>
            <button type="button" onClick={() => navigate('/admin/campaigns')}>Все →</button>
          </div>

          <div className="card-list">
            {recentCampaigns.length > 0 ? (
              recentCampaigns.map((c) => (
                <div
                  key={c.id}
                  className="list-item"
                  onClick={() => navigate(`/admin/campaigns/${c.id}`)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="item-main">
                    <span className="item-name">{c.name || 'Без названия'}</span>
                    <span className="item-meta">
                      {c.business_email?.split('@')[0]} • {formatDate(c.created_at)}
                    </span>
                  </div>

                  <div className="item-badges">
                    <span className={`badge badge-type ${c.type}`}>{c.type}</span>
                    <span className={`badge badge-status status-${c.status}`}>{c.status}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">Нет кампаний</div>
            )}
          </div>
        </div>

        {/* Recent Leads */}
        <div className="dash-card">
          <div className="card-header">
            <h3>Новые лиды</h3>
            <button type="button" onClick={() => navigate('/admin/leads')}>Все →</button>
          </div>

          <div className="card-list">
            {recentLeads.length > 0 ? (
              recentLeads.map((l) => (
                <div key={l.id} className="list-item" role="button" tabIndex={0}>
                  <div className="item-main">
                    <span className="item-name">{l.first_name || l.user_email || `Лид #${l.id}`}</span>
                    <span className="item-meta">
                      {l.user_email} • {formatDate(l.created_at)}
                    </span>
                  </div>

                  <div className="item-badges">
                    <span className={`badge badge-lead lead-${l.type}`}>
                      {l.type === 'direct' ? 'Direct • €20' : 'Referral • €20'}
                    </span>
                    {l.is_verified && <span className="badge badge-verified">Verified</span>}
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">Нет лидов</div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="dash-card quick-actions">
          <div className="card-header">
            <h3>Быстрые действия</h3>
          </div>

          <div className="actions-grid">
            <button type="button" onClick={() => navigate('/admin/subscriptions')}>Подписки</button>
            <button type="button" onClick={() => navigate('/admin/wallets')}>Wallets</button>
            <button type="button" onClick={() => navigate('/admin/brand-gifts')}>AI Gifts</button>
            <button type="button" onClick={() => navigate('/admin/promocodes')}>Промокоды</button>
            <button type="button" onClick={() => navigate('/admin/leads')}>Лиды</button>
            <button type="button" onClick={() => navigate('/admin/commissions')}>Комиссии</button>
            <button type="button" onClick={() => navigate('/admin/campaigns')}>Кампании</button>
            <button type="button" onClick={() => navigate('/admin/users')}>Юзеры</button>
            <button type="button" onClick={() => navigate('/admin/businesses')}>Бизнесы</button>
          </div>
        </div>

        {/* Platform Info */}
        <div className="dash-card platform-info">
          <div className="card-header">
            <h3>DARY Platform</h3>
          </div>

          <div className="platform-content">
            <div className="platform-row">
              <span>Версия</span>
              <span className="platform-value">Strategy 2026 v2.0</span>
            </div>
            <div className="platform-row">
              <span>API</span>
              <span className="platform-value">Admin v4.0.0</span>
            </div>
            <div className="platform-row">
              <span>CPL Model</span>
              <span className="platform-value">€20/лид</span>
            </div>
            <div className="platform-row">
              <span>Subscriptions</span>
              <span className="platform-value">€249/mo • €2490/yr</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
